import React from "react";
import { observer } from "mobx-react-lite";
import { container } from "@/utils/mobx";
import { ActivityBarStore } from "./store";
import { SettingsModal } from "./SettingsModal";
import { SetupAgentModal } from "./SetupAgentModal";
import { Clickable } from "@/components/toolkit/Clickable";
import { t } from "@/i18n";
import "./s.activity_bar.css";


const ZoomSlider = observer(({ store }: { store: ActivityBarStore }) => {
    const trackRef = React.useRef<HTMLDivElement>(null);

    const updateZoomFromY = (clientY: number) => {
        const track = trackRef.current;
        if (!track) return;
        const rect = track.getBoundingClientRect();
        const t = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
        store.setUiZoom(Math.round(200 - t * 150));
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault();
        (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
        updateZoomFromY(e.clientY);
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (e.buttons === 0) return;
        updateZoomFromY(e.clientY);
    };

    const knobT = (200 - store.uiZoom) / 150;

    return (
        <div className="activity_bar__zoom_control">
            <button
                className="activity_bar__zoom_close_btn"
                onClick={() => store.closeZoom()}
                title="Close zoom"
            >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
            </button>
            <div
                ref={trackRef}
                className="activity_bar__zoom_track"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
            >
                <div className="activity_bar__zoom_fill" style={{ height: `${(1 - knobT) * 100}%` }} />
                <div className="activity_bar__zoom_knob" style={{ top: `${knobT * 100}%` }} />
            </div>
            <span className="activity_bar__zoom_label">{store.uiZoom}%</span>
        </div>
    );
});

export const ActivityBar = container((props: {
    store: ActivityBarStore;
    section: "code" | "review" | "deploy";
    onSectionChange: (s: "code" | "review" | "deploy") => any;
    isAgentOpen: boolean;
    onAgentToggle: () => any;
}) => {
    const store = props.store;
    const account = store.account;
    const initials = account
        ? account.full_name
            .split(" ")
            .map(w => w[0])
            .join("")
            .slice(0, 2)
            .toUpperCase()
        : "?";

    // On mobile, Agent acts as a full-section tab — suppress Code/Review active state when agent is open
    const sectionActive = !store.isMobile || !store.isAgentOpen;

    return (
        <aside className="activity_bar" style={{ zoom: `${100 / store.uiZoom}` }}>
            {store.selectedProjectId && (
            <nav className="activity_bar__nav">
                <Clickable captureTouch onClick={() => store.setSection("code")}>
                    <button
                        className={"activity_bar__item" + (sectionActive && store.section === "code" ? " active" : "")}
                        title={t("nav_code")}
                    >
                        <span className="activity_bar__item_label">{t("nav_code")}</span>
                    </button>
                </Clickable>
                <Clickable captureTouch onClick={() => store.setSection("review")}>
                    <button
                        className={"activity_bar__item" + (sectionActive && store.section === "review" ? " active" : "")}
                        title={t("nav_review")}
                    >
                        <span className="activity_bar__item_label">{t("nav_review")}</span>
                    </button>
                </Clickable>
                <Clickable captureTouch onClick={() => store.setSection("deploy")}>
                    <button
                        className={"activity_bar__item" + (sectionActive && store.section === "deploy" ? " active" : "")}
                        title={t("nav_deploy")}
                    >
                        <span className="activity_bar__item_label">{t("nav_deploy")}</span>
                    </button>
                </Clickable>
                <div className="activity_bar__separator" />
                <Clickable captureTouch onClick={() => store.toggleAgent()}>
                    <button
                        className={"activity_bar__item" + (store.isAgentOpen ? " active" : "")}
                        title={t("nav_agent")}
                    >
                        <span className="activity_bar__item_label">{t("nav_agent")}</span>
                    </button>
                </Clickable>
            </nav>
            )}

            <div className="activity_bar__bottom">
                {store.isZoomOpen ? (
                    <ZoomSlider store={store} />
                ) : (
                    <>
                        <Clickable onClick={() => store.toggleAccountMenu()}>
                            <button
                                className="activity_bar__account_btn"
                                aria-label={t("account_menu_label")}
                            >
                                <span className="activity_bar__avatar">{initials}</span>
                            </button>
                        </Clickable>

                        <div className="activity_bar__account_dropdown" data-open={store.isAccountMenuOpen}>
                            <div className="activity_bar__account_backdrop" onMouseDown={() => setTimeout(() => store.closeAccountMenu(), 300)} onTouchStart={() => setTimeout(() => store.closeAccountMenu(), 300)} />
                            <div className="activity_bar__account_menu">
                                <div className="activity_bar__account_info">
                                    <div className="activity_bar__account_info_row">
                                        <div className="activity_bar__account_info_text">
                                            <span className="activity_bar__account_name">{account?.full_name}</span>
                                            <span className="activity_bar__account_email">{account?.email}</span>
                                        </div>
                                        <Clickable onClick={() => store.refreshApp()}>
                                            <button className="activity_bar__refresh_btn" title="Refresh app">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="23 4 23 10 17 10"/>
                                                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                                                </svg>
                                            </button>
                                        </Clickable>
                                        <Clickable onClick={() => window.open(window.location.href, '_blank', 'toolbar=0,location=0,menubar=0,width=' + window.screen.availWidth + ',height=' + window.screen.availHeight)}>
                                            <button className="activity_bar__refresh_btn activity_bar__new_window_btn" title="Open in new window">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                                                    <path d="M9 3v18"/>
                                                </svg>
                                            </button>
                                        </Clickable>
                                        <Clickable onClick={() => store.openZoom()}>
                                            <button className="activity_bar__refresh_btn" title="Zoom">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="11" cy="11" r="8"/>
                                                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                                    <line x1="8" y1="11" x2="14" y2="11"/>
                                                    <line x1="11" y1="8" x2="11" y2="14"/>
                                                </svg>
                                            </button>
                                        </Clickable>
                                    </div>
                                </div>
                                <div className="activity_bar__account_divider" />
                                {store.projects.length === 0 && (
                                    <div className="activity_bar__project_menu_empty">{t("account_no_projects")}</div>
                                )}
                                {store.projects.map(project => (
                                    <Clickable key={project.id} onClick={() => store.selectProject(project.id)}>
                                        <button className={"activity_bar__project_menu_item" + (store.selectedProjectId === project.id ? " active" : "")}>
                                            {project.name}
                                        </button>
                                    </Clickable>
                                ))}
                                {store.selectedProjectId && (
                                    <Clickable onClick={() => store.closeProject()}>
                                        <button className="activity_bar__project_menu_close">
                                            {t("account_close_project")}
                                        </button>
                                    </Clickable>
                                )}
                                <div className="activity_bar__account_divider" />
                                <Clickable onClick={() => store.openSettingsModal()}>
                                    <button className="activity_bar__settings_btn">{t("account_settings")}</button>
                                </Clickable>
                                <Clickable onClick={() => store.openSetupAgentModal()}>
                                    <button className="activity_bar__settings_btn">{t("account_setup_agent")}</button>
                                </Clickable>
                                <Clickable onClick={() => store.signOut()}>
                                    <button className="activity_bar__sign_out">{t("account_sign_out")}</button>
                                </Clickable>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <SettingsModal store={store} />

            <SetupAgentModal store={store} />
        </aside>
    );
}, ActivityBarStore);
